import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material'
import ContentCopy from '@mui/icons-material/ContentCopy'
import ResponsiveDialog from './ResponsiveDialog'
import StatusBadge from '../StatusBadge'
import {
  TerminalLogViewer,
  type LogFetchResult,
  type TerminalLogViewerHandle,
} from './TerminalLogViewer'
import { activityAPI } from '../../services/api'

export interface JobWithLogs {
  id: string | number
  status: string
  type?: string
}

export type LogViewerFetchLogs = (offset: number) => Promise<LogFetchResult>

interface LogViewerDialogProps<T extends JobWithLogs> {
  job: T | null
  open: boolean
  onClose: () => void
  jobTypeLabel?: string // Optional: "Backup", "Check", "Compact", etc.
  title?: string
  onFetchLogs?: LogViewerFetchLogs
}

export default function LogViewerDialog<T extends JobWithLogs>({
  job,
  open,
  onClose,
  jobTypeLabel,
  title,
  onFetchLogs,
}: LogViewerDialogProps<T>) {
  const { t } = useTranslation()
  // Determine job type for API endpoint (default to 'backup' for backward compatibility)
  const jobType = job?.type || 'backup'
  const jobId = job?.id

  // Determine display label
  const displayLabel =
    jobTypeLabel || (job?.type ? getTypeLabel(job.type, t) : t('logViewer.typeBackup'))

  const logViewerRef = useRef<TerminalLogViewerHandle>(null)

  // Track live status so the badge and log viewer update after the job finishes
  const [currentStatus, setCurrentStatus] = useState(job?.status || 'unknown')

  // Reset status whenever a different job is opened
  useEffect(() => {
    setCurrentStatus(job?.status || 'unknown')
  }, [job?.id, job?.status])

  // Poll the activity list every 3 s while the dialog is open and the job is still running
  useEffect(() => {
    if (onFetchLogs || !open || !jobId || currentStatus !== 'running') return
    const poll = async () => {
      try {
        const response = await activityAPI.list({ job_type: jobType, limit: 100 })
        const items: Array<{ id: number; type: string; status: string }> = response.data
        const item = items.find((i) => String(i.id) === String(jobId) && i.type === jobType)
        if (item && item.status !== 'running') setCurrentStatus(item.status)
      } catch {
        // Ignore network errors — next poll will retry
      }
    }
    const interval = setInterval(poll, 3000)
    return () => clearInterval(interval)
  }, [onFetchLogs, open, jobId, jobType, currentStatus])

  // Memoize the fetch function to prevent re-renders from causing duplicate log fetches
  const handleFetchLogs = useCallback(
    async (offset: number) => {
      if (!jobId) return { lines: [], total_lines: 0, has_more: false }
      if (onFetchLogs) return onFetchLogs(offset)

      const response = await activityAPI.getLogs(jobType, jobId, offset)
      return response.data
    },
    [jobType, jobId, onFetchLogs]
  )

  if (!job) return null

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      footer={
        <DialogActions>
          <Button onClick={onClose}>{t('dialogs.logViewer.close')}</Button>
        </DialogActions>
      }
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            {title || t('logViewer.title', { label: displayLabel, jobId: job.id })}
          </Typography>
          <StatusBadge status={currentStatus} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title={t('terminalLogViewer.copyLogs')}>
            <IconButton
              size="small"
              aria-label={t('terminalLogViewer.copyLogs')}
              onClick={() => logViewerRef.current?.copyLogs()}
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <TerminalLogViewer
          ref={logViewerRef}
          jobId={String(job.id)}
          status={currentStatus}
          jobType={jobType}
          showHeader={false}
          onFetchLogs={handleFetchLogs}
        />
      </DialogContent>
    </ResponsiveDialog>
  )
}

// Helper to get human-readable type labels
function getTypeLabel(type: string, t: (key: string) => string): string {
  switch (type) {
    case 'backup':
      return t('logViewer.typeBackup')
    case 'restore':
      return t('logViewer.typeRestore')
    case 'check':
      return t('logViewer.typeCheck')
    case 'restore_check':
      return t('scheduledRestoreChecks.badge.restoreCheck')
    case 'compact':
      return t('logViewer.typeCompact')
    case 'prune':
      return t('logViewer.typePrune')
    case 'package':
      return t('logViewer.typePackage')
    case 'rclone_sync':
      return t('logViewer.typeRcloneSync')
    case 'rclone_hydrate':
      return t('logViewer.typeRcloneHydrate')
    case 'script_execution':
      return t('logViewer.typeScript')
    default:
      return type.charAt(0).toUpperCase() + type.slice(1)
  }
}
